import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { HasHistory } from "@insite/mobius/utilities/HistoryContext";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Hidden from "@insite/mobius/Hidden";
import { CustomSettingCollectionModel } from "@NA/Store/CustomTypeModel/CustomSettingCollectionModel";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { css } from "styled-components";
import NoCameraIcon from "@NA/Icons/NoCameraIcon";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import translate from "@insite/client-framework/Translate";
import ToggleCameraIcon from "@NA/Icons/ToggleCameraIcon";
import ADIColors from "@NA/Utils/ADIColors";

const mapStateToProps = (state: CustomApplicationState) => {
    return {
        settingsCollection: getSettingsCollection(state) as unknown as CustomSettingCollectionModel,
    };
};

const mapDispatchToProps = {};

interface BarcodeWidgetStyles {
    barcodeImgStyling?: LazyImageProps;
    cameraNotFoundPopUpContainer?: GridContainerProps;
    leftSideGridItem?: GridItemProps;
    rightSideGridItem?: GridItemProps;
    camNotFoundHeaderText?: TypographyProps;
    camNotFoundSubText?: TypographyProps;
    captureVideoDiv?: InjectableCss;
}

export const barcodeWidgetStyles: BarcodeWidgetStyles = {
    barcodeImgStyling: {
        css: css`
            margin-left: 5px;
            > img {
                height: auto;
                width: auto;
            }
        `,
    },
    cameraNotFoundPopUpContainer: {
        css: css`
            position: fixed;
            top: 90px;
            left: 0;
            z-index: 2;
            background: ${ADIColors.white};
            padding: 10px;
            box-shadow: 3px 3px 5px 0px ${ADIColors.lightBlackIcon};

            svg {
                width: 53px;
            }
            .cameraSt0 {
                stroke: ${ADIColors.iconRed};
                stroke-width: 3;
                fill: ${ADIColors.iconRed};
            }

            .cameraSt1 {
                fill: none;
                stroke: ${ADIColors.white};
                stroke-width: 2;
                stroke-miterlimit: 10;
            }

            .cameraSt2 {
                fill: none;
                stroke: ${ADIColors.white};
                stroke-width: 2;
            }
        `,
    },
    leftSideGridItem: {
        css: css`
            justify-content: right;
            padding-right: 0px;
        `,
    },
    rightSideGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    camNotFoundHeaderText: {
        css: css`
            font-weight: bold;
            font-size: 22px;
            color: ${ADIColors.iconRed};
        `,
    },
    camNotFoundSubText: {
        css: css`
            color: ${ADIColors.iconRed};
            text-align: left;
        `,
    },
    captureVideoDiv: {
        css: css`
            #scanner-mobile-container {
                position: absolute;
                top: 0;
                left: 0;
                width: 100vw;
                height: 100vh;
                background: ${ADIColors.lightBlackIcon};
                z-index: 10000;
                text-align: center;
                vertical-align: middle;
            }

            #scanner-mobile-container > #closeVideo {
                font-size: 2em;
                padding: 0;
                color: ${ADIColors.black};
                background: ${ADIColors.white};
                border-radius: 50%;
                box-shadow: 0.1em 0.05em 0.05em ${ADIColors.lightBlackIcon};
                position: absolute;
                top: 0.2em;
                right: 0.2em;
                z-index: 100001;
                width: 43px;
                height: 43px;
            }

            #scanner-mobile-container > #toggleCamera {
                position: absolute;
                top: 0.2em;
                left: 0.6em;
                z-index: 100001;
            }

            #scanner-mobile-container > #toggleCamera > svg {
                filter: drop-shadow(0.1em 0.05em 0.05em ${ADIColors.black});
            }

            #scanner-mobile-container > video {
                width: 100%;
                margin: 0 auto;
                position: relative;
            }

            @media screen and (max-width: 420px) {
                #popup-barcode.reveal-modal,
                .reveal-modal .modal-wrap.dark {
                    min-width: 100vw;
                    max-width: 100vw;
                    min-height: 100vw;
                    max-height: 100vh;
                    top: 8vh !important;
                    left: 0;
                }

                .reveal-modal .modal-wrap.dark {
                    min-width: 100vw;
                    max-width: 100vw;
                    min-height: 100vw;
                    max-height: 100vh;
                }

                #scanner-video-container {
                    height: 100%;
                    text-align: center;
                    position: relative;
                }

                #scanner-video-container > video {
                    height: 100vw;
                    transform: rotate(90deg);
                    transform-origin: center center;
                }

                #popup-barcode.reveal-modal,
                dialog {
                    top: 3%;
                }
            }

            .tCameraSt0 {
                fill: #${ADIColors.white};
            }
            .tCameraSt1 {
                fill: ${ADIColors.black};
            }
        `,
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = barcodeWidgetStyles;

declare global {
    interface Window {
        Dynamsoft: any;
    }
}

interface ScriptLoadResult {
    script: string;
    loaded: boolean;
    status: string;
}

const { useState, useEffect } = React;

interface DynamsoftData {
    cameraView: any;
    cameraEnhancer: any;
    router: any;
}

const ADIDynamsoftBarcodeReader: React.FC<Props> = ({ settingsCollection }) => {
    const [bcrOrganizationID, setBcrOrganizationID] = useState<string>("");
    const [dbrCDNURL, setDbrCDNURL] = useState<string>("");
    const [bcrLicenseKey, setBcrLicenseKey] = useState<string>("");
    const [barcodeEnabled, setBarcodeEnabled] = useState<boolean>(false);
    const [mobileOnly, setMobileOnly] = useState<boolean>(false);
    const [isMobileApp, setIsMobileApp] = useState<boolean>(false);
    const [showButton, setShowButton] = useState<boolean>(false);
    const [settingsLoadedAndAssigned, setSettingsLoadedAndAssigned] = useState(false);
    const [showNoCamFoundPopUp, setShowNoCamFoundPopUp] = useState<boolean>(false);
    const [enableCamera, setEnableCamera] = useState<boolean>(false);
    const [videoIdsInfoUpdated, setVideoIdsInfoUpdated] = useState<boolean | undefined>(undefined);
    const [Dynamsoft, setDynamsoft] = useState<any>(undefined);

    const [dynamsoftData, setDynamsoftData] = useState<DynamsoftData>({
        cameraView: {},
        cameraEnhancer: {},
        router: {},
    });

    const barcodeImageSrc =
        "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjUiIGhlaWdodD0iMjUiIHZpZXdCb3g9IjAgMCAyNSAyNSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICAgIDxnIGNsaXAtcGF0aD0idXJsKCNhb242cm82eWhhKSI+CiAgICAgICAgPHBhdGggZD0iTTEyLjMwMyAxOC4zNTRWNi42NWgtMS4yOHYxMS43MDJoMS4yOHpNMjUgMTkuOTMzaC0xLjYzOXYzLjQ0aC0zLjQyN3YxLjYyMkgyNXYtNS4wNjJ6TTEuNjM1IDUuMDY4di0zLjQ0aDMuNDM0VjBIMHY1LjA2OGgxLjYzNHpNMTkuOTMgMHYxLjYyN2gzLjQ0djMuNDM1SDI1VjBoLTUuMDd6TTUuMDY0IDI1di0xLjYzNUgxLjYyOXYtMy40MjlIMFYyNWg1LjA2NHptMTUuNDM0LTYuNjQ1VjYuNjVoLS45NTN2MTEuNzA1aC45NTN6TTQuNTAyIDYuNjQ4VjE4LjM1aC45NTRWNi42NDhoLS45NTR6bTMuNTA4IDB2MTEuNzA0aC42MjdWNi42NDhIOC4wMXptMi4yOTguMDA2aC0uNjM2VjE4LjM1aC42MzZWNi42NTR6bTUuMDE0IDExLjd2LTExLjdoLS42MjN2MTEuN2guNjIzem0zLjM0NSAwdi0xMS43aC0uNjI0djExLjdoLjYyNHpNNi41IDYuNjQ5VjE4LjM1aC4yOThWNi42NDhoLS4yOTh6bTYuNjg2IDB2MTEuNjk4aC4zVjYuNjQ4aC0uM3ptMy4zOSAwVjE4LjM1aC4yMTZWNi42NDhoLS4yMTV6IiBmaWxsPSIjZmZmIi8+CiAgICAgICAgPHBhdGggZD0iTTEyLjMwMyAxOC4zNTRoLTEuMjhWNi42NWgxLjI4djExLjcwMnpNMjUgMTkuOTMzdjUuMDYzaC01LjA2NnYtMS42MjNoMy40Mjd2LTMuNDRIMjV6TTEuNjM1IDUuMDY4SDBWMEg1LjA3djEuNjI3SDEuNjM1djMuNDQxek0xOS45MyAwSDI1djUuMDYyaC0xLjYzVjEuNjI3aC0zLjQ0VjB6TTUuMDY0IDI1SDB2LTUuMDY0aDEuNjI5djMuNDI5aDMuNDM1VjI1ek0yMC40OTggMTguMzU1aC0uOTUzVjYuNjVoLjk1M3YxMS43MDV6TTQuNTAyIDYuNjQ4aC45NTRWMTguMzVoLS45NTRWNi42NDh6TTguMDEgNi42NDhoLjYyN3YxMS43MDRIOC4wMVY2LjY0OHpNMTAuMzA4IDYuNjU0VjE4LjM1aC0uNjM2VjYuNjU0aC42MzZ6TTE1LjMyMiAxOC4zNTRoLS42MjN2LTExLjdoLjYyM3YxMS43ek0xOC42NjcgMTguMzU1aC0uNjI1VjYuNjUzaC42MjV2MTEuNzAyek02LjUgNi42NDhoLjI5N1YxOC4zNWgtLjI5OFY2LjY0OHpNMTMuMTg1IDYuNjQ4aC4zdjExLjY5OGgtLjNWNi42NDh6TTE2LjU3NiA2LjY0OGguMjE1VjE4LjM1aC0uMjE1VjYuNjQ4eiIgZmlsbD0iIzAwMjg1NiIvPgogICAgPC9nPgogICAgPGRlZnM+CiAgICAgICAgPGNsaXBQYXRoIGlkPSJhb242cm82eWhhIj4KICAgICAgICAgICAgPHBhdGggZmlsbD0iI2ZmZiIgZD0iTTAgMGgyNXYyNUgweiIvPgogICAgICAgIDwvY2xpcFBhdGg+CiAgICA8L2RlZnM+Cjwvc3ZnPgo=";
    const bcrEngineResourcePath: string = "/Scripts/Libraries/dbr-js/8.2.5/dbr.js"; // "https://cdn.jsdelivr.net/npm/dynamsoft-javascript-barcode@7.3.0-v4/dist/dbr.js";
    useEffect(() => {
        setIsMobileApp(navigator.userAgent.indexOf("[UsableNet Lift Mobile]") !== -1);

        const { customerSettings } = settingsCollection;

        let tempDRBCDNRUL;
        let orgId = customerSettings.dynamsoftOrganizationID;
        let cdnUrl = customerSettings.dbrJSURL;
        let licenseKey = customerSettings.dynamsoftLicenseKey;
        let barcode_enabled = customerSettings.enableUPCScanner;
        let mobile_only = customerSettings.setUPCScannerToMobileOnly;

        if (!cdnUrl) {
            tempDRBCDNRUL = bcrEngineResourcePath;
        } else {
            tempDRBCDNRUL = cdnUrl;
        }

        setBcrOrganizationID(orgId);
        setDbrCDNURL(tempDRBCDNRUL);
        setBcrLicenseKey(licenseKey);
        setBarcodeEnabled(barcode_enabled);
        setMobileOnly(mobile_only);
        setSettingsLoadedAndAssigned(true);
    }, []);

    useEffect(() => {
        if (settingsLoadedAndAssigned) {
            load();
        }
    }, [settingsLoadedAndAssigned]);

    useEffect(() => {
        const initializeDynamsoft = async () => {
            if (Dynamsoft) {
                Dynamsoft.License.LicenseManager.initLicense(bcrLicenseKey);
                const routerInstance = await Dynamsoft.CVR.CaptureVisionRouter.createInstance();
                setDynamsoftData(prevState => ({
                    ...prevState,
                    router: routerInstance,
                }));
            }
        };

        initializeDynamsoft();
    }, [Dynamsoft]);

    useEffect(() => {
        if (videoIdsInfoUpdated) {
            createInstance();
        }
    }, [videoIdsInfoUpdated]);

    function isMobile() {
        let isMobile = window.innerWidth <= 1024;
        return isMobile;
    }

    function allowButton() {
        let allowButton = false;
        if (settingsCollection) {
            let a = settingsCollection.customerSettings.enableUPCScanner;
            a =
                !isMobileApp &&
                a &&
                (!settingsCollection.customerSettings.setUPCScannerToMobileOnly ||
                    (settingsCollection.customerSettings.setUPCScannerToMobileOnly && isMobile()));
            allowButton = a;
        } else {
            allowButton = false;
        }
        setBarcodeEnabled(allowButton);
        return allowButton;
    }

    const show = () => {
        if (Dynamsoft) {
            Dynamsoft.DBR.BarcodeScanner.detectEnvironment().then((env: any) => {
                if (!env.camera) {
                    setTimeout(() => {
                        setShowNoCamFoundPopUp(false);
                    }, 3000);
                    setShowNoCamFoundPopUp(true);
                } else if (env.camera && env.worker && env.wasm) {
                    setVideoIdsInfoUpdated(true);
                    setEnableCamera(true);
                }
            });
        }
    };

    function load() {
        loadScript().then(result => {
            if (result.loaded) {
                setDynamsoft(window["Dynamsoft"]);
                setShowButton(allowButton());
            }
        });
    }

    function loadScript() {
        return new Promise<ScriptLoadResult>((resolve, reject) => {
            if (!window.Dynamsoft) {
                const script = document.createElement("script");

                script.type = "text/javascript";
                script.id = "dbrScript";
                script.setAttribute("data-productKeys", bcrLicenseKey);
                script.setAttribute("data-organizationID", bcrOrganizationID);
                script.src = dbrCDNURL;

                script.onload = () => {
                    resolve({ script: "name", loaded: true, status: "Loaded" });
                };

                script.onerror = error => reject({ script: "name", loaded: false, status: "Error" });
                document.getElementsByTagName("head")[0].appendChild(script);
            } else {
                resolve({ script: "name", loaded: true, status: "Already Loaded" });
            }
        });
    }

    const createInstance = async () => {
        const { router } = dynamsoftData;
        if (!router) {
            return null;
        }

        let cameraView = await Dynamsoft.DCE.CameraView.createInstance(document.getElementById("enhancerUIContainer"));
        let cameraEnhancer = await Dynamsoft.DCE.CameraEnhancer.createInstance(cameraView);
        setDynamsoftData(prevState => ({
            ...prevState,
            cameraView,
            cameraEnhancer,
        }));

        router.setInput(cameraEnhancer);
        router.addResultReceiver({
            onDecodedBarcodesReceived: (result: { barcodeResultItems: string | any[] }) => {
                if (result.barcodeResultItems.length > 0) {
                    for (let item of result.barcodeResultItems) {
                        window.location.href = "/search?criteria=" + item;
                    }
                }
            },
        });

        let filter = new Dynamsoft.Utility.MultiFrameResultCrossFilter();
        filter.enableResultCrossVerification(Dynamsoft.Core.EnumCapturedResultItemType.CRIT_BARCODE, true);
        filter.enableResultDeduplication(Dynamsoft.Core.EnumCapturedResultItemType.CRIT_BARCODE, true);
        await router.addResultFilter(filter);

        await cameraEnhancer.open();
        await router.startCapturing("ReadSingleBarcode");
    };

    const getCurrentCamera = async () => {
        try {
            const { cameraEnhancer } = dynamsoftData;
            const currentCamera = await cameraEnhancer.getCurrentCamera();
            return currentCamera;
        } catch (error) {
            throw error;
        }
    };

    const handleCloseCamera = async () => {
        setEnableCamera(false);
        const { cameraEnhancer } = dynamsoftData;
        await cameraEnhancer.close();
        setVideoIdsInfoUpdated(undefined);
    };

    const toggleCamera = async () => {
        const { cameraEnhancer } = dynamsoftData;
        const cameras = await cameraEnhancer.getAllCameras();
        if (cameras.length > 1) {
            getCurrentCamera().then(async cur => {
                let curCam = cameras.find((c: { label: any }) => c.label === cur.label);
                let current = cameras.indexOf(curCam);
                current = current === -1 ? 0 : current;
                let next = current + 1;
                next = next >= cameras.length ? 0 : next;
                await cameraEnhancer.selectCamera(cameras[next]);
            });
        }
    };

    return (
        <>
            <Hidden above="sm">
                <LazyImage onClick={show} {...styles.barcodeImgStyling} src={barcodeImageSrc} />
            </Hidden>
            {showNoCamFoundPopUp && (
                <GridContainer {...styles.cameraNotFoundPopUpContainer}>
                    <GridItem {...styles.leftSideGridItem} width={4}>
                        <NoCameraIcon />
                    </GridItem>
                    <GridItem {...styles.rightSideGridItem} width={8}>
                        <Typography {...styles.camNotFoundHeaderText}>{translate("Camera Not Found !")}</Typography>
                        <Typography {...styles.camNotFoundSubText}>
                            {translate("If your device has a camera, please enable it.")}
                        </Typography>
                    </GridItem>
                </GridContainer>
            )}
            {enableCamera ? (
                <StyledWrapper {...styles.captureVideoDiv}>
                    <div id="enhancerUIContainer" style={{ width: "100%", height: "100%" }}>
                        <span onClick={handleCloseCamera} id="closeVideo">
                            &#215;
                        </span>
                        <span onClick={toggleCamera} id="toggleCamera">
                            <ToggleCameraIcon />
                        </span>
                        <div className="dce-video-container" style={{ width: "100%", height: "100%" }}></div>
                        <div
                            className="dce-scanarea"
                            style={{
                                position: "absolute",
                                left: "0px",
                                top: "0px",
                                width: "1280px",
                                height: "720px",
                                pointerEvents: "none",
                            }}
                        >
                            <div
                                className="dce-scanlight"
                                style={{
                                    display: "block",
                                    position: "absolute",
                                    width: "100%",
                                    height: "3%",
                                    borderRadius: "50%",
                                    boxShadow: "0 0 2vw 1px rgb(0, 229, 255)",
                                    background: "rgb(255, 255, 255)",
                                    animation: "dce-scanlight 3s ease infinite",
                                    userSelect: "none",
                                }}
                            ></div>
                        </div>
                    </div>
                </StyledWrapper>
            ) : null}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ADIDynamsoftBarcodeReader),
    definition: {
        displayName: "ADI Dynamsoft Barcode Reader",
        group: "Basic",
    },
};

export default widgetModule;
